import { useState } from "react";
import { MdOutlineImage } from "react-icons/md";
import { PiPackageLight } from "react-icons/pi";
import { locationName } from "../FulfilmentLocation/useWarehouseChange";
import "./ProductCells.css";

// The cells of the My Products list (ProductListView) after the redesign: four
// columns in place of Image, Name, Sku, Dimensions, Weight, Quantity and one
// column per location. Look only: each value, fallback and sum is the one the
// old columns showed, and a click still goes to the row (BoltTable's
// handleRowClick adds the product).

export const primaryImage = (row) =>
  row?.media?.find((media) => media.is_primary === "true")?.file ||
  row?.media?.[0]?.file;

// True when all three sides are above 0 (a missing size shows "Not set").
export const hasSize = (length, width, height) =>
  parseFloat(length) > 0 && parseFloat(width) > 0 && parseFloat(height) > 0;

// Holding Enter on a button repeats the key, and the button clicks on every
// repeat: this keeps it to one add per press (Space clicks once, on release).
// For the lists' photo buttons and the grids' tiles.
export const onePressOnly = (e) => {
  if (e.key === "Enter" && e.repeat) e.preventDefault();
};

// A row's photo, or `icon` when it has none or it fails to load (it was the
// noImage picture). It is a button only so the row can be reached and added
// from the keyboard (SP-069): it does not add anything itself; its click
// (mouse, Enter or Space) goes on to the row, which adds it once, as a click on
// the old image did. Used by the Products, Bundles and Packages lists.
export function PhotoButton({ name, url, icon }) {
  const [failedUrl, setFailedUrl] = useState(null);
  const showPhoto = Boolean(url) && failedUrl !== url;
  return (
    <button
      type="button"
      className="myprod__addrow"
      aria-label={`Add ${name}`}
      onKeyDown={onePressOnly}
      onClick={(e) => {
        // A mouse click (detail > 0) leaves no focus on the button, as the old
        // image could not take it; otherwise a later Space or Enter would add
        // the product again. Enter / Space keep focus here for keyboard users.
        if (e.detail > 0) e.currentTarget.blur();
      }}
    >
      {showPhoto ? (
        <img src={url} alt="" loading="lazy" onError={() => setFailedUrl(url)} />
      ) : (
        <span className="myprod__addrow-ic">{icon}</span>
      )}
    </button>
  );
}

// The product's photo, or a plain image icon.
export function AddCell({ row }) {
  return (
    <PhotoButton
      name={row?.name ?? "product"}
      url={primaryImage(row)}
      icon={<MdOutlineImage aria-hidden="true" />}
    />
  );
}

// Name, then SKU (the old Name and Sku columns).
export function ProductCell({ row }) {
  return (
    <div className="myprod__cell myprod__product">
      <span className="myprod__pri">{row?.name ?? "-"}</span>
      <span className="myprod__sec myprod__sku">{row?.code ?? "-"}</span>
    </div>
  );
}

// A list cell's size line: "L*W*H (UNIT)", or "Not set" (amber, with a hover
// note) when a side isn't above 0, as on the grid tiles. `children` follow
// it (the products' box icon and package count).
export function SizeLine({ row, unit, kind, children }) {
  const sized = hasSize(row?.length, row?.width, row?.height);
  return (
    <span
      className={`myprod__pri myprod__dims${sized ? "" : " is-missing"}`}
      title={sized ? undefined : `No size saved for this ${kind} yet`}
    >
      {sized ? `${row.length}*${row.width}*${row.height}${unit ? ` (${unit})` : ""}` : "Not set"}
      {children}
    </span>
  );
}

// L*W*H (UNIT) with the package count, then the weight (the old Dimensions
// and Weight columns; a missing size showed 0*0*0, or null*null*null for
// SP-059).
export function SizeWeightCell({ row }) {
  const packages =
    row?.package_details && Object.values(row.package_details).length > 0
      ? Object.values(row.package_details).length
      : 0;
  return (
    <div className="myprod__cell myprod__size">
      <SizeLine row={row} unit={row?.dimension_unit?.toUpperCase() ?? "N/A"} kind="product">
        <span
          className="myprod__box"
          title={packages > 0 ? `Packed in ${packages} package${packages > 1 ? "s" : ""}` : undefined}
        >
          <PiPackageLight aria-hidden="true" />
          {packages > 0 && <span className="myprod__pkg-badge">{packages}</span>}
        </span>
      </SizeLine>
      <span className="myprod__sec">
        {row?.weight ?? "0"} {row?.weight_unit}
      </span>
    </div>
  );
}

// The total, then the quantity at each approved location that has a record
// for the product (the old Quantity column and one column per location, where
// the others were empty), in the Fulfilment Location card's order. The total
// is the old column's sum as it was, including its quirk (KNOWN_ISSUES.md
// SP-070). The chosen location is always listed ("—" without a record) and
// highlighted (its quantity was bold).
export const stockTotal = (row) =>
  row?.product_warehouse?.reduce(
    (total, warehouse) => total + parseInt(warehouse.quantity, 10) || 0,
    0
  );

// The locations the Stock line lists (also the grid tile's hover text).
export function stockLocations(row, warehouses, selected) {
  const hasChoice = selected !== "" && selected !== null && selected !== undefined;
  return (warehouses ?? []).flatMap((entry) => {
    // Loose == as in the old columns: ids come as numbers or strings.
    const match = row?.product_warehouse?.find(
      // eslint-disable-next-line eqeqeq
      (item) => item?.warehouse_id == entry?.warehouse_id
    );
    // eslint-disable-next-line eqeqeq
    const chosen = hasChoice && entry?.warehouse_id == selected;
    if (!match && !chosen) return [];
    return [
      {
        key: entry?.warehouse_id,
        name: locationName(entry),
        quantity: match ? match.quantity ?? "-" : "—",
        chosen,
      },
    ];
  });
}

export function StockCell({ row, warehouses, selected }) {
  const total = stockTotal(row);
  const locations = stockLocations(row, warehouses, selected);
  return (
    <div className="myprod__cell myprod__stock">
      <span className="myprod__pri">{total}</span>
      {locations.length > 0 && (
        <span className="myprod__sec myprod__locs">
          {locations.map((location, index) => (
            <span
              key={location.key ?? index}
              className={`myprod__loc-qty${location.chosen ? " is-chosen" : ""}`}
            >
              {index > 0 && <i className="myprod__dot" aria-hidden="true" />}
              {location.name} <b>{location.quantity}</b>
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
